import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Trash2, Phone, Send, Calendar, Check, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EnquiryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [enquiry, setEnquiry] = useState(null);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');

  useEffect(() => {
    fetchEnquiry();
    fetchComments();
    fetchUsers();
    fetchBranches();
  }, [id]);

  const fetchEnquiry = async () => {
    try {
      const response = await axios.get(`${API}/enquiries/${id}`);
      setEnquiry(response.data);
      setFollowUpDate(response.data.follow_up_date || '');
    } catch (error) {
      toast.error('Failed to load enquiry');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/enquiry-comments/enquiry/${id}`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to load comments');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data.filter(u => u.role === 'sales_executive' || u.role === 'manager' || u.role === 'admin'));
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`);
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to load branches');
    }
  };

  // Filter users based on selected branch
  const filteredUsers = useMemo(() => {
    if (!enquiry?.branch_id) {
      return users; // Show all users if no branch selected
    }
    // Filter to show only users from the selected branch, plus admins (who have no branch restriction)
    return users.filter(u => u.branch_id === enquiry.branch_id || u.role === 'admin' || !u.branch_id);
  }, [users, enquiry?.branch_id]);

  const handleUpdateEnquiry = async (field, value) => {
    setUpdating(true);
    try {
      const updatePayload = { [field]: value };
      
      // If branch is being changed, clear the assigned_to if the user doesn't belong to the new branch
      if (field === 'branch_id' && enquiry?.assigned_to) {
        const assignedUser = users.find(u => u.id === enquiry.assigned_to);
        if (assignedUser && assignedUser.branch_id && assignedUser.branch_id !== value && assignedUser.role !== 'admin') {
          updatePayload.assigned_to = null;
          toast.info('Assigned user cleared as they do not belong to the new branch');
        }
      }
      
      await axios.put(`${API}/enquiries/${id}`, updatePayload);
      toast.success('Enquiry updated');
      fetchEnquiry();
    } catch (error) {
      toast.error('Failed to update enquiry');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      await axios.post(`${API}/enquiry-comments`, {
        enquiry_id: id,
        comment: newComment
      });
      setNewComment('');
      fetchComments();
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleSaveFollowUpDate = async () => {
    setUpdating(true);
    try {
      await axios.put(`${API}/enquiries/${id}`, { follow_up_date: followUpDate || null });
      toast.success('Follow-up date updated');
      setEditingFollowUp(false);
      fetchEnquiry();
    } catch (error) {
      toast.error('Failed to update follow-up date');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelFollowUpEdit = () => {
    setFollowUpDate(enquiry?.follow_up_date || '');
    setEditingFollowUp(false);
  };

  const isEnquiryClosed = enquiry?.status === 'won' || enquiry?.status === 'lost';

  const handleDeleteEnquiry = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/enquiries/${id}`);
      toast.success('Enquiry deleted successfully');
      navigate('/enquiries');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete enquiry');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-900',
      contacted: 'bg-indigo-100 text-indigo-900',
      qualified: 'bg-purple-100 text-purple-900',
      proposal_sent: 'bg-white text-slate-900 border border-slate-300',
      won: 'bg-green-100 text-green-900',
      lost: 'bg-red-100 text-red-900',
    };
    return colors[status] || 'bg-slate-100 text-slate-900';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-slate-100 text-slate-900',
      medium: 'bg-white text-slate-900 border border-slate-300',
      high: 'bg-red-100 text-red-900',
    };
    return colors[priority] || 'bg-slate-100 text-slate-900';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <Navbar setSidebarOpen={setSidebarOpen} />
      
      <main className="md:ml-64 pt-16">
        <div className="p-6 md:p-8">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-4"
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Enquiries
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Enquiry Details */}
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="enquiry-detail-card">
                <h1 className="text-3xl font-bold text-slate-900 mb-4">{enquiry?.product_name}</h1>
                <div className="flex items-center gap-2 mb-6">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(enquiry?.status)}`}>
                    {enquiry?.status?.replace('_', ' ')}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(enquiry?.priority)}`}>
                    {enquiry?.priority}
                  </span>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Price Details</p>
                    <p className="text-slate-700">{enquiry?.price_details || 'Not specified'}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Customer</p>
                      <div className="flex items-center gap-3">
                        <p className="text-slate-900 font-medium">{enquiry?.customer_name || 'Unknown'}</p>
                        {enquiry?.customer_phone && (
                          <a href={`tel:${enquiry.customer_phone}`} className="flex items-center gap-1 text-base font-semibold text-blue-600 hover:text-blue-800">
                            <Phone className="h-4 w-4" />
                            {enquiry.customer_phone}
                          </a>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Created Date</p>
                      <p className="text-slate-900">{new Date(enquiry?.created_at).toLocaleDateString()}</p>
                    </div>
                    {enquiry?.created_by_name && (
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Created By</p>
                        <p className="text-slate-900">{enquiry.created_by_name}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Next Follow-up Date</p>
                      {!isEnquiryClosed ? (
                        editingFollowUp ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="date"
                              value={followUpDate}
                              onChange={(e) => setFollowUpDate(e.target.value)}
                              className="w-40 h-8"
                              data-testid="followup-date-input"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={handleSaveFollowUpDate}
                              disabled={updating}
                              data-testid="save-followup-btn"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                              onClick={handleCancelFollowUpEdit}
                              disabled={updating}
                              data-testid="cancel-followup-btn"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div 
                            className="flex items-center gap-2 cursor-pointer group"
                            onClick={() => setEditingFollowUp(true)}
                            data-testid="followup-date-display"
                          >
                            <Calendar className="h-4 w-4 text-slate-400 group-hover:text-blue-500" />
                            <span className="text-slate-900 group-hover:text-blue-600">
                              {enquiry?.follow_up_date ? new Date(enquiry.follow_up_date).toLocaleDateString() : 'Click to set date'}
                            </span>
                            <span className="text-xs text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">(click to edit)</span>
                          </div>
                        )
                      ) : (
                        <p className="text-slate-900">
                          {enquiry?.follow_up_date ? new Date(enquiry.follow_up_date).toLocaleDateString() : 'Not set'}
                        </p>
                      )}
                    </div>
                    {enquiry?.closed_at && (
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Closed Date</p>
                        <p className={`font-medium ${enquiry?.status === 'won' ? 'text-green-700' : 'text-red-700'}`}>
                          {new Date(enquiry.closed_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {enquiry?.remarks && (
                      <div className="col-span-2">
                        <p className="text-xs text-slate-500 mb-1">Remarks</p>
                        <p className="text-slate-700">{enquiry.remarks}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="comments-card">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">Comments</h2>
                
                <div className="space-y-4 mb-6">
                  {comments.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No comments yet</p>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.id} className="border border-slate-200 rounded-lg p-4" data-testid={`comment-${comment.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-accent text-white flex items-center justify-center text-sm font-semibold">
                              {comment.user_name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{comment.user_name}</p>
                              <p className="text-xs text-slate-500">{formatDate(comment.created_at)}</p>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{comment.comment}</p>
                      </div>
                    ))
                  )}
                </div>

                <form onSubmit={handleAddComment} className="space-y-4">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    data-testid="new-comment-input"
                  />
                  <Button type="submit" data-testid="submit-comment-button">
                    <Send className="h-4 w-4 mr-2" />
                    Add Comment
                  </Button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Status */}
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="enquiry-status-card">
                <h3 className="text-sm font-medium text-slate-500 mb-3">Status</h3>
                <Select 
                  value={enquiry?.status} 
                  onValueChange={(value) => handleUpdateEnquiry('status', value)}
                  disabled={updating}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Branch - Before Assign To */}
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="enquiry-branch-card">
                <h3 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Branch
                </h3>
                {user?.role === 'admin' ? (
                  <Select 
                    value={enquiry?.branch_id || 'unassigned'} 
                    onValueChange={(value) => handleUpdateEnquiry('branch_id', value === 'unassigned' ? null : value)}
                    disabled={updating}
                  >
                    <SelectTrigger data-testid="branch-select">
                      <SelectValue placeholder="Select Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">No Branch</SelectItem>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="px-3 py-2 bg-slate-100 rounded-md text-sm text-slate-700 font-medium">
                    {enquiry?.branch_name || 'No Branch Assigned'}
                  </div>
                )}
              </div>

              {/* Assign To - Filtered by Branch */}
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="enquiry-assign-card">
                <h3 className="text-sm font-medium text-slate-500 mb-3">Assigned To</h3>
                <Select 
                  value={enquiry?.assigned_to || 'unassigned'} 
                  onValueChange={(value) => handleUpdateEnquiry('assigned_to', value === 'unassigned' ? null : value)}
                  disabled={updating}
                >
                  <SelectTrigger data-testid="assign-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {filteredUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} {u.branch_id && enquiry?.branch_id && u.branch_id !== enquiry.branch_id ? '(Other Branch)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {enquiry?.branch_id && (
                  <p className="text-xs text-slate-500 mt-2">
                    Showing members from selected branch
                  </p>
                )}
              </div>

              {/* Admin Delete */}
              {user?.role === 'admin' && (
                <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="delete-enquiry-card">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" className="w-full" disabled={deleting} data-testid="delete-enquiry-button">
                        <Trash2 className="h-4 w-4 mr-2" />
                        {deleting ? 'Deleting...' : 'Delete Enquiry'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Enquiry</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this enquiry for <strong>{enquiry?.product_name}</strong>? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteEnquiry} className="bg-red-600 hover:bg-red-700">
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
